export type ActorSpriteKind = 'self' | 'remotePlayer' | 'mob';

export function actorColorForKind(kind: ActorSpriteKind): string {
  if (kind === 'self') return '#3b82f6';
  if (kind === 'mob') return '#ef4444';
  return '#f59e0b';
}

export function drawActorSprite(
  ctx: CanvasRenderingContext2D,
  options: {
    centerX: number;
    centerY: number;
    size: number;
    rotation: number;
    color: string;
    accessoriesJson?: string;
    alpha?: number;
  }
): void {
  const { centerX, centerY, size, rotation, color } = options;
  const alpha = options.alpha ?? 1;
  const accessories = parseAccessories(options.accessoriesJson);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.shadowColor = color;
  ctx.shadowBlur = size * 0.42;
  ctx.beginPath();
  ctx.arc(centerX, centerY, size / 3, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha * 0.8;
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.arc(centerX, centerY, size / 5, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.fill();

  drawAccessories(ctx, centerX, centerY, size, rotation, accessories);
  drawEyesWithGlow(ctx, centerX, centerY, size, rotation);
  ctx.restore();
}

function parseAccessories(accessoriesJson?: string): { head?: string; held?: string } {
  if (!accessoriesJson) return {};
  try {
    const parsed = JSON.parse(accessoriesJson) as { head?: unknown; held?: unknown };
    return {
      head: typeof parsed.head === 'string' ? parsed.head : undefined,
      held: typeof parsed.held === 'string' ? parsed.held : undefined,
    };
  } catch {
    return {};
  }
}

function drawAccessories(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  rotation: number,
  accessories: { head?: string; held?: string }
): void {
  const radians = (rotation * Math.PI) / 180;
  const forwardX = Math.sin(radians);
  const forwardY = -Math.cos(radians);
  const rightX = Math.cos(radians);
  const rightY = Math.sin(radians);

  if (accessories.head === 'hood') {
    ctx.beginPath();
    ctx.arc(centerX, centerY - size * 0.08, size * 0.24, Math.PI, Math.PI * 2);
    ctx.fillStyle = 'rgba(15, 118, 110, 0.95)';
    ctx.fill();
  }

  if (accessories.head === 'wizard_hat') {
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - size * 0.42);
    ctx.lineTo(centerX + size * 0.18, centerY - size * 0.08);
    ctx.lineTo(centerX - size * 0.18, centerY - size * 0.08);
    ctx.closePath();
    ctx.fillStyle = 'rgba(91, 33, 182, 0.95)';
    ctx.fill();
  }

  if (accessories.head === 'helm') {
    ctx.beginPath();
    ctx.arc(centerX, centerY - size * 0.08, size * 0.23, Math.PI, Math.PI * 2);
    ctx.fillStyle = 'rgba(100, 116, 139, 0.96)';
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - size * 0.28);
    ctx.lineTo(centerX, centerY - size * 0.02);
    ctx.strokeStyle = 'rgba(226, 232, 240, 0.9)';
    ctx.lineWidth = size * 0.025;
    ctx.stroke();
  }

  if (accessories.head === 'horns') {
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(centerX + side * size * 0.08, centerY - size * 0.18);
      ctx.lineTo(centerX + side * size * 0.22, centerY - size * 0.36);
      ctx.lineTo(centerX + side * size * 0.02, centerY - size * 0.28);
      ctx.closePath();
      ctx.fillStyle = 'rgba(120, 53, 15, 0.94)';
      ctx.fill();
    }
  }

  const heldX = centerX + rightX * size * 0.22 + forwardX * size * 0.06;
  const heldY = centerY + rightY * size * 0.22 + forwardY * size * 0.06;
  if (accessories.held === 'bow') {
    ctx.beginPath();
    ctx.arc(heldX, heldY, size * 0.12, -Math.PI * 0.7, Math.PI * 0.7);
    ctx.strokeStyle = 'rgba(187, 247, 208, 0.95)';
    ctx.lineWidth = size * 0.028;
    ctx.stroke();
  }
  if (accessories.held === 'staff') {
    ctx.beginPath();
    ctx.moveTo(heldX - rightX * size * 0.05, heldY - rightY * size * 0.05);
    ctx.lineTo(heldX + rightX * size * 0.11, heldY + rightY * size * 0.11);
    ctx.strokeStyle = 'rgba(226, 232, 240, 0.92)';
    ctx.lineWidth = size * 0.028;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(heldX + rightX * size * 0.12, heldY + rightY * size * 0.12, size * 0.04, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(196, 181, 253, 0.95)';
    ctx.fill();
  }
  if (accessories.held === 'shield') {
    ctx.beginPath();
    ctx.rect(heldX - size * 0.08, heldY - size * 0.08, size * 0.14, size * 0.18);
    ctx.fillStyle = 'rgba(148, 163, 184, 0.92)';
    ctx.fill();
  }
  if (accessories.held === 'sword') {
    ctx.beginPath();
    ctx.moveTo(heldX - rightX * size * 0.03, heldY - rightY * size * 0.03);
    ctx.lineTo(heldX + rightX * size * 0.12, heldY + rightY * size * 0.12);
    ctx.strokeStyle = 'rgba(226, 232, 240, 0.95)';
    ctx.lineWidth = size * 0.026;
    ctx.stroke();
  }
}

function drawEyesWithGlow(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  size: number,
  rotation: number
): void {
  const eyeForwardOffset = size / 4;
  const eyeSideOffset = size / 8;
  const eyeRadius = size / 12;
  const radians = (rotation * Math.PI) / 180;
  const forwardX = Math.sin(radians);
  const forwardY = -Math.cos(radians);
  const rightX = Math.cos(radians);
  const rightY = Math.sin(radians);

  const leftEyeX = centerX + forwardX * eyeForwardOffset - rightX * eyeSideOffset;
  const leftEyeY = centerY + forwardY * eyeForwardOffset - rightY * eyeSideOffset;
  const rightEyeX = centerX + forwardX * eyeForwardOffset + rightX * eyeSideOffset;
  const rightEyeY = centerY + forwardY * eyeForwardOffset + rightY * eyeSideOffset;

  ctx.save();
  ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
  ctx.shadowBlur = size * 0.25;
  ctx.fillStyle = 'white';
  ctx.beginPath();
  ctx.arc(leftEyeX, leftEyeY, eyeRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(rightEyeX, rightEyeY, eyeRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

